import React, { useState } from 'react';
import { Globe, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, PDFPreview, NoteCard } from '../shared';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import '../../styles/design-system.css';

const HTMLtoPDF = () => {
    const [url, setUrl] = useState('');
    const [htmlContent, setHtmlContent] = useState('');
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [mode, setMode] = useState('url'); // 'url' or 'upload' or 'paste'
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            const text = await selectedFile.text();
            setHtmlContent(text);
            toast.success('HTML file loaded');
        }
    };

    const convertToPDF = async () => {
        try {
            setIsProcessing(true);
            setProgress(10);

            let content = htmlContent;

            if (mode === 'url' && url) {
                toast.info('Fetching URL content...');
                try {
                    const response = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`);
                    content = await response.text();
                    setProgress(30);
                } catch (error) {
                    toast.error('Failed to fetch URL. Please try pasting HTML instead.');
                    setIsProcessing(false);
                    return;
                }
            }

            if (!content) {
                toast.warning('Please provide HTML content');
                setIsProcessing(false);
                return;
            }

            setProgress(50);

            // Create a temporary container
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.width = '800px';
            container.innerHTML = content;
            document.body.appendChild(container);

            setProgress(70);

            // Convert to canvas
            const canvas = await html2canvas(container, {
                scale: 2,
                useCORS: true,
                logging: false
            });

            // Remove container
            document.body.removeChild(container);

            setProgress(85);

            // Create PDF
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const imgWidth = 210; // A4 width in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

            setProgress(95);

            // Get blob
            const blob = pdf.output('blob');

            setProgress(100);

            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = `html-to-pdf-${timestamp}.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('HTML converted! Opening preview...');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Failed to convert: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Globe size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    HTML to PDF
                </h1>
                <p className="tool-description">
                    Convert HTML content or web pages to PDF format. Enter a URL, upload HTML file, or paste HTML code.
                </p>
            </div>

            {/* Mode Selection */}
            <Card>
                <h3 style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-bold)',
                    marginBottom: 'var(--spacing-4)'
                }}>
                    Input Method
                </h3>

                <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
                    <Button
                        variant={mode === 'url' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setMode('url')}
                    >
                        URL
                    </Button>
                    <Button
                        variant={mode === 'upload' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setMode('upload')}
                    >
                        Upload HTML
                    </Button>
                    <Button
                        variant={mode === 'paste' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setMode('paste')}
                    >
                        Paste HTML
                    </Button>
                </div>

                {mode === 'url' && (
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-medium)',
                            marginBottom: 'var(--spacing-2)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            Website URL
                        </label>
                        <input
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://example.com"
                            className="input"
                        />
                    </div>
                )}

                {mode === 'upload' && (
                    <FileUploadZone
                        onFilesSelected={handleFileSelected}
                        acceptedTypes={['.html', '.htm', 'text/html']}
                        multiple={false}
                        title="Drop HTML file here"
                        description="or click to browse"
                    />
                )}

                {mode === 'paste' && (
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-medium)',
                            marginBottom: 'var(--spacing-2)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            HTML Code
                        </label>
                        <textarea
                            value={htmlContent}
                            onChange={(e) => setHtmlContent(e.target.value)}
                            placeholder="Paste your HTML code here..."
                            className="input"
                            rows={10}
                            style={{ fontFamily: 'monospace', fontSize: 'var(--text-sm)' }}
                        />
                    </div>
                )}
            </Card>

            {/* Preview */}
            {htmlContent && mode !== 'url' && (
                <div style={{ marginTop: 'var(--spacing-6)' }}>
                    <Card>
                        <h3 style={{
                            fontSize: 'var(--text-lg)',
                            fontWeight: 'var(--font-bold)',
                            marginBottom: 'var(--spacing-4)'
                        }}>
                            HTML Preview
                        </h3>
                        <div
                            style={{
                                maxHeight: '400px',
                                overflowY: 'auto',
                                padding: 'var(--spacing-4)',
                                backgroundColor: 'var(--color-surface-elevated)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border)'
                            }}
                            dangerouslySetInnerHTML={{ __html: htmlContent }}
                        />
                    </Card>
                </div>
            )}

            {/* Progress Bar */}
            {isProcessing && (
                <div style={{ marginTop: 'var(--spacing-6)' }}>
                    <ProgressBar value={progress} striped showLabel />
                </div>
            )}

            {/* Convert Button */}
            <div style={{ marginTop: 'var(--spacing-6)' }}>
                <Button
                    variant="primary"
                    size="lg"
                    onClick={convertToPDF}
                    disabled={isProcessing || (!url && !htmlContent)}
                    loading={isProcessing}
                    fullWidth
                >
                    <Download size={20} />
                    Convert to PDF
                </Button>
            </div>

            {/* Info */}
            <NoteCard variant="info">
                <strong>Note:</strong> URL conversion may be limited by CORS policies. For best results, upload HTML files or paste HTML code directly.
            </NoteCard>

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default HTMLtoPDF;
